import ScrollCarousel from '../../components/ScrollCarousel';

// Make an index of carousels for potential use later in theme editor
const pods_carousels = {};

// Without this class the pods stay at `opacity: 0` — i.e. invisible. It must
// land on every code path, so it is applied up front rather than only from the
// carousel's `ready` callback.
const LOADED_CLASS = 'AnimateOnLoad--loaded';

const SELECTORS = {
  viewport: '[data-pods-carousel-viewport]',
  track: '[data-pods-carousel-track]',
};

const CLASSES = {
  viewport: 'PodsCarousel__viewport',
  track: 'PodsCarousel__track',
  arrow: 'PodsCarousel__arrow',
  arrowPrev: 'PodsCarousel__arrow--previous',
  arrowNext: 'PodsCarousel__arrow--next',
  arrowDisabled: 'PodsCarousel__arrow--disabled',
  dots: 'PodsCarousel__dots',
  dot: 'PodsCarousel__dot',
  dotActive: 'PodsCarousel__dot--active',
  pause: 'PodsCarousel__pause',
};

/**
 * How many pods fit in one viewport width. Measured rather than derived from
 * the `PodsCarousel--N-column` class, because the column count is overridden
 * per breakpoint in SCSS and several of the ten sections hard-code their own.
 */
const measureSlidesPerView = (viewport, pod) => {
  const podWidth = pod.offsetWidth;
  const viewportWidth = viewport.clientWidth;
  if (!podWidth || !viewportWidth) return 1;
  return Math.max(1, Math.round(viewportWidth / podWidth));
};

// The main function that actually initializes the carousels
const initPodsCarousel = (carousel, index) => {
  if (!carousel) {
    return;
  }

  const id = carousel.id;
  const data = carousel.dataset;

  let initial_index = index || 0;
  let prev_next_buttons = data.prevNextButtons === 'true';
  let page_dots = data.pageDots === 'true';
  let draggable = data.draggable === 'true';
  let wrap_around = data.wrapAround === 'true';
  let group_cells = data.groupCells === 'true';

  if (window.theme.breakpoints.current === 'mobile') {
    prev_next_buttons = data.prevNextButtonsMobile === 'true';
    page_dots = data.pageDotsMobile === 'true';
    draggable = data.draggableMobile === 'true';
    wrap_around = data.wrapAroundMobile === 'true';
    group_cells = data.groupCellsMobile === 'true';
  }

  if (pods_carousels[id]) {
    // Preserve the visible slide across the teardown so a breakpoint change or
    // a theme-editor reload doesn't reset the carousel to the first pod.
    initial_index = pods_carousels[id].selectedIndex;
    pods_carousels[id].destroy();
    delete pods_carousels[id];
  }

  // Applied before every early return below — the pods are invisible without it.
  carousel.classList.add(LOADED_CLASS);

  const viewport = carousel.querySelector(SELECTORS.viewport);
  const track = carousel.querySelector(SELECTORS.track);

  if (!viewport || !track || track.children.length < 1) {
    // Don't init pointless carousels with no slides
    return;
  }

  const slides_per_view = measureSlidesPerView(viewport, track.children[0]);

  // Nothing overflows the viewport, so there is nothing to navigate to. This
  // replaces the old wrapped-float detection, which only worked because
  // Flickity laid the cells out with `float: left`.
  const all_pods_visible = track.children.length <= slides_per_view;

  if (all_pods_visible) {
    wrap_around = false;
    prev_next_buttons = false;
    draggable = false;
    page_dots = false;
  }

  pods_carousels[id] = new ScrollCarousel(carousel, {
    selectors: SELECTORS,
    classes: CLASSES,
    initialIndex: initial_index,
    prevNextButtons: prev_next_buttons,
    pageDots: page_dots,
    loop: wrap_around,
    slidesPerView: slides_per_view,
    // Flickity's `groupCells: true` advanced a full viewport at a time.
    slidesPerGroup: group_cells ? slides_per_view : 1,
    // Pods autoplay was already commented out under Flickity; keep it off.
    // `data-auto-play` here is emitted as the string "false" or a number, and
    // wiring it up would be a visible behavior change pending a PM decision.
    // Ref. WIN-8070.
    autoPlay: 0,
    adaptiveHeight: true,
    draggable,
    ariaLabel: 'Pods carousel',
    // `cellAlign` and `contain` were Flickity layout concerns; both are
    // handled by `scroll-snap-align` / `scroll-padding` in INF-Pods.scss.
  });
};

const initPodsCarousels = () => {
  document.querySelectorAll('[data-pods-carousel]').forEach((carousel) => {
    initPodsCarousel(carousel);
  });
};

export { pods_carousels, initPodsCarousel, initPodsCarousels };
